import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const oceanBlueThemeOptions: ThemeOptions = {
  name: "oceanBlue",
  palette: {
    mode: "dark",
    primary: { main: rawColor.oceanBlue.main },
    secondary: { main: rawColor.oceanBlue.secondary },
    background: {
      default: rawColor.oceanBlue.background,
      paper: rawColor.oceanBlue.paper,
    },
    text: {
      primary: rawColor.oceanBlue.text.primary,
      secondary: rawColor.oceanBlue.text.secondary,
    },
    action: {
      hover: rawColor.oceanBlue.sidebar.hover,
    },
    error: { main: rawColor.status.error.dark },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.green },
    divider: rawColor.oceanBlue.divider,
  },
  sidebar: {
    base: colors.sidebar.oceanBlue.base,
    hover: colors.sidebar.oceanBlue.hover,
    selected: colors.sidebar.oceanBlue.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.oceanBlue.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.oceanBlue.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.oceanBlue.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.oceanBlue.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.oceanBlue.cell,
          color: rawColor.oceanBlue.text.primary,
        },
        head: {
          backgroundColor: colors.table.oceanBlue.header,
          color: colors.table.oceanBlue.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.oceanBlue.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.oceanBlue.text.primary,
        },
        h6: {
          color: rawColor.oceanBlue.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.oceanBlue.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.oceanBlue.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.oceanBlue.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.oceanBlue.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.oceanBlue.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.oceanBlue.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.oceanBlue.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.oceanBlue.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.oceanBlue.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.oceanBlue.text.secondary,
          "&.Mui-focused": {
            color: rawColor.oceanBlue.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.oceanBlue.text.secondary,
        },
        select: {
          color: rawColor.oceanBlue.text.secondary,
        },
        selectLabel: {
          color: rawColor.oceanBlue.text.secondary,
        },
        selectIcon: {
          color: rawColor.oceanBlue.text.secondary,
        },
        displayedRows: {
          color: rawColor.oceanBlue.text.secondary,
        },
        actions: {
          color: rawColor.oceanBlue.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.oceanBlue.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.oceanBlue.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.oceanBlue.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.oceanBlue.sidebar.selected,
              color: rawColor.oceanBlue.main,
              "&:hover": {
                backgroundColor: rawColor.oceanBlue.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.oceanBlue.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.oceanBlue.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.oceanBlue.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.oceanBlue.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.oceanBlue.paper} !important`,
          borderColor: `${rawColor.oceanBlue.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.oceanBlue.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.oceanBlue.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.oceanBlue.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.oceanBlue.paper} !important`,
          border: `1px solid ${rawColor.oceanBlue.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.oceanBlue.paper} !important`,
          color: `${rawColor.oceanBlue.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.oceanBlue.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.oceanBlue.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.oceanBlue.sidebar.selected} !important`,
            color: `${rawColor.oceanBlue.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.oceanBlue.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.oceanBlue.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.oceanBlue.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.oceanBlue.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.oceanBlue.sidebar.selected} !important`,
              color: `${rawColor.oceanBlue.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.oceanBlue.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.oceanBlue.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.oceanBlue.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.oceanBlue.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.oceanBlue.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.oceanBlue.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.oceanBlue.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.oceanBlue.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.oceanBlue.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.oceanBlue.main} !important`,
          },
        },
      },
    },
  },
};

export const oceanBlueTheme = createTheme(oceanBlueThemeOptions);
